import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Info, Search, UploadCloud, RotateCcw, ChevronDown, Building2, Hash, Clock3, Gauge, Download, Zap, MapPin, Ticket, FileText, Route, Paperclip, FolderOpen } from "lucide-react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { fetchAccountHistory, getFieldNames, generateTaskPdfLink } from "../../service/Task";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import CommonButton from "../../components/common/CommonButton";
import HoverTooltip from "../../components/common/HoverTooltip";
import moment from "moment";
import StatusPill from "../../components/common/StatusPill";
import ColumnMap from "./ColumnMap";
import TaskRoadmapDrawer from "./TaskRoadmapDrawer";
import * as XLSX from "xlsx";
import { getPermissionByScreenCode, isSuperAdmin } from "../../utils/permissions";
import { isRTL } from "../../utils/rtl";
import TableScrollShell from "../../components/common/TableScrollShell";
import ReactSelector from "../../components/common/CommonReactSelector";
import AttachmentList from "../../components/common/AttachmentList";
import { findAttachment } from "../../utils/attachments";
import { normalizeAccountHistoryValue, collectAccountAttachments, countWorkflowTasks } from "../../utils/accountHistory";

const ACCOUNT_HISTORY_STORAGE_KEY = "accountHistoryLookupState";

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

// Account-details stat card styling helpers — presentational only.
const detailCardClass = (theme, wide, accent) =>
  `flex items-start gap-2.5 rounded-[10px] border p-3 ${wide ? "sm:col-span-2" : ""} ${
    theme === "dark"
      ? accent
        ? "border-emerald-500/20 bg-emerald-500/10"
        : "border-gray-600 bg-gray-700/40"
      : theme === "high-contrast"
        ? "high-contrast border-2"
        : accent
          ? "border-emerald-100 bg-emerald-50"
          : "border-gray-200 bg-gray-50"
  }`;

const detailIconChipClass = (theme, accent) =>
  `flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] ${
    theme === "dark"
      ? accent
        ? "bg-emerald-500/20 text-emerald-400"
        : "bg-gray-600/70 text-gray-300"
      : theme === "high-contrast"
        ? "high-contrast"
        : accent
          ? "bg-emerald-100 text-emerald-600"
          : "border border-gray-200 bg-white text-[#236DB4]"
  }`;

const detailLabelClass = (theme) =>
  `mb-0.5 text-[11px] font-semibold uppercase tracking-wide ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`;

const detailValueClass = (theme) =>
  `text-sm font-semibold break-words ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`;

// Picks a field-appropriate icon for a dynamic accountDetailsJson key based on common
// naming patterns (consumer name, account no., subscription no., provider, code/type/area,
// etc.) instead of showing the same generic "#" icon for every field.
const getDetailFieldIcon = (key) => {
  const k = String(key || "").toLowerCase().replace(/[^a-z]/g, "");
  if (k === "area") return MapPin;
  if (k.includes("name")) return Building2;
  if (k.includes("subscription")) return Ticket;
  if (k.includes("provider")) return Zap;
  if (k.includes("code") || k.includes("type")) return MapPin;
  if (k.includes("no") || k.includes("number") || k.includes("id")) return Hash;
  return FileText;
};

// "Finished Date"/"Finished Time" only mean something once a task is
// actually done -- for Active/Assigned/In Progress rows those fields are
// always null, so those two columns fall back to the upload/created date
// instead of showing a bare "-".
const isFinishedTaskStatus = (status) => {
  const normalized = String(status ?? "").trim().toLowerCase();
  return normalized === "completed" || normalized === "finished";
};

const HISTORY_STATUS_FILTER = {
  ALL: "all",
  FINISHED: "finished",
  NON_FINISHED: "nonFinished",
};

const matchesHistoryStatusFilter = (status, filter) => {
  if (filter === HISTORY_STATUS_FILTER.FINISHED) return isFinishedTaskStatus(status);
  if (filter === HISTORY_STATUS_FILTER.NON_FINISHED) return !isFinishedTaskStatus(status);
  return true;
};

const formatTimeOnly = (timeString) => {
  if (!timeString) return "-";
  return new Date(`1970-01-01T${timeString}`).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Same avatar-badge-stack pattern already used for Task Management's own
// "Assigned To" column (src/components/task/TaskListView.js), adapted to take
// a plain array of names (Account History's API returns one comma-separated
// string per row, not assignee objects) and to use HoverTooltip instead of
// the native `title` attribute for the full-list hover.
const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#0891b2", "#16a34a", "#ea580c", "#db2777", "#4f46e5", "#0d9488"];
const avatarColorFor = (name) => AVATAR_COLORS[(name || "?").charCodeAt(0) % AVATAR_COLORS.length];

const AssigneeAvatarStack = ({ names, size = 22 }) => {
  const list = Array.isArray(names) ? names.filter(Boolean) : [];
  if (!list.length) return <span className="text-slate-400">-</span>;
  const shown = list.length > 2 ? list.slice(0, 2) : list;
  const overflow = list.length - shown.length;
  const fullList = list.join(", ");
  const tooltipList = (
    <ul className="flex flex-col gap-1">
      {list.map((name, i) => (
        <li key={i} className="flex items-center gap-1.5">
          <span
            className="w-4 h-4 shrink-0 rounded-full flex items-center justify-center text-white font-medium"
            style={{ background: avatarColorFor(name), fontSize: 9 }}
          >
            {String(name).trim().charAt(0).toUpperCase()}
          </span>
          <span className="truncate">{name}</span>
        </li>
      ))}
    </ul>
  );
  return (
    <HoverTooltip text={tooltipList} className="relative inline-flex items-center gap-1.5 justify-start cursor-default">
      <div className="flex -space-x-1.5 shrink-0">
        {shown.map((name, i) => (
          <div
            key={i}
            className="rounded-full flex items-center justify-center text-white font-medium shrink-0 border border-white"
            style={{ background: avatarColorFor(name), width: size, height: size, fontSize: size * 0.42 }}
          >
            {String(name).trim().charAt(0).toUpperCase()}
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="rounded-full flex items-center justify-center text-slate-600 bg-slate-200 border border-white font-semibold shrink-0"
            style={{ width: size, height: size, fontSize: size * 0.4 }}
          >
            +{overflow}
          </div>
        )}
      </div>
      <span className="text-xs text-slate-500 truncate max-w-[100px]">{fullList}</span>
    </HoverTooltip>
  );
};

const TaskHistory = ({ headerSlot = null }) => {
  const [accountNumber, setAccountNumber] = useState("");
  // One entry per permitted project the account exists in: { projectId, projectName, departmentName, details }.
  const [accountDepartments, setAccountDepartments] = useState([]);
  const [mapOpen, setMapOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const context = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const { langMode, theme, selectedOrgId } = context;
  const isRtl = isRTL(langMode);
  const [mandatoryCols, setMandatoryCols] = useState([]);
  const [otherCols, setOtherCols] = useState([]);
  const [file, setFile] = useState(null);
  const [colHeaders, setColHeaders] = useState([]);
  const fileInputRef = useRef(null);
  const customVarPerm = getPermissionByScreenCode("ACCOUNT_HISTORY");
  // Purely presentational — which per-workflow history sections are collapsed.
  // Starts empty so every section renders open, matching the previous always-expanded behavior.
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());
  const toggleSection = (key) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  // Per-accordion free-text filter for that section's own table, keyed by
  // sectionKey so each workflow's search box is independent of the others.
  const [sectionSearch, setSectionSearch] = useState({});
  const [sectionStatusFilter, setSectionStatusFilter] = useState({});
  // Guards the persistence effect below from firing with the initial empty
  // state before the hydration effect has had a chance to restore anything
  // from sessionStorage (mirrors TaskListToolbar's searchHydratedRef).
  const hydratedRef = useRef(false);
  // Task roadmap side drawer. `roadmapTaskId` is deliberately left set while
  // the drawer slides out so its content doesn't blank mid-animation.
  const [roadmapTaskId, setRoadmapTaskId] = useState(null);
  const [roadmapOpen, setRoadmapOpen] = useState(false);
  // taskId -> S3 link returned by the on-request PDF generation, so re-clicking
  // a row's download button reuses the link instead of regenerating it.
  const [generatedPdfLinks, setGeneratedPdfLinks] = useState({});
  const pdfRequestsInFlightRef = useRef(new Set());

  const openRoadmap = (taskId) => {
    if (!taskId) return;
    setRoadmapTaskId(taskId);
    setRoadmapOpen(true);
  };

  const accountNumberFetch = (e) => {
    const value = e.target.value;
    setAccountNumber(value);
    if (!value) {
      // Box fully cleared by hand -- treat like Reset for the fetched data.
      setAccountDepartments([]);
    }
  };

  const handleChange = (e) => {
    console.log(e.target.value);
  };

  const clearData = () => {
    setAccountNumber("");
    setAccountDepartments([]);
    sessionStorage.removeItem(ACCOUNT_HISTORY_STORAGE_KEY);
  };

  // Restore a previously fetched account lookup (survives tab switches --
  // this component fully unmounts when ExportData.js swaps to the Export
  // Report tab -- and page refreshes) without re-fetching from the API.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ACCOUNT_HISTORY_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Lookups saved by an older layout have no accountDepartments, so they are dropped.
        if (parsed?.accountNumber && Array.isArray(parsed.accountDepartments)) {
          setAccountNumber(parsed.accountNumber);
          setAccountDepartments(parsed.accountDepartments);
        }
      }
    } catch (error) {
      console.error("Error restoring account history state:", error);
    } finally {
      hydratedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      if (!accountNumber) {
        sessionStorage.removeItem(ACCOUNT_HISTORY_STORAGE_KEY);
        return;
      }
      sessionStorage.setItem(
        ACCOUNT_HISTORY_STORAGE_KEY,
        JSON.stringify({ accountNumber, accountDepartments })
      );
    } catch (error) {
      console.error("Error saving account history state:", error);
    }
  }, [accountNumber, accountDepartments]);

  // No project/department is sent: the backend returns every one the user is permitted to see. A super
  // admin is not limited to assigned projects, so the selected organization scopes the search instead.
  const requestAccountHistory = () =>
    fetchAccountHistory(accountNumber, null, isSuperAdmin() ? selectedOrgId : null);

  const fetchTaskHistory = async () => {
    if (!accountNumber) {
      toast.error('Please Enter account number to see Account History');
      return;
    }
    setIsLoading(true);
    try {
      const responseData = await requestAccountHistory();
      if (responseData?.header?.code === 600) {
        setAccountDepartments(normalizeAccountHistoryValue(responseData.body.value));
        toast.success(translations[langMode].datafetchedsuccessfully);
      } else if (responseData?.header?.code === 601) {
        const backendMessage =
          responseData?.body?.value ??
          responseData?.body?.error ??
          responseData?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
      else {
        toast.error(responseData.body.value);
      }
    } catch (error) {
      console.error("Error fetching task history:", error);
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Attachment downloadUrls are presigned for ~60 minutes, and the rows on
  // screen may have been restored from sessionStorage long after the fetch,
  // so every Download re-runs the history call and takes that response's link.
  const resolveFreshAttachment = async (attachment) => {
    const responseData = await requestAccountHistory();
    if (responseData?.header?.code !== 600) return null;
    const everyAttachment = collectAccountAttachments(
      normalizeAccountHistoryValue(responseData.body?.value)
    );
    return findAttachment(everyAttachment, attachment);
  };

  // Opens the centred popup used for task PDFs. `pdfLink` may be omitted to
  // open a blank window first (see handleDownloadPdf) and point it at the URL
  // once the backend answers. Returns the window handle so the caller can
  // navigate/close it, or null when the browser blocked the popup.
  const openPdfWindow = (pdfLink) => {
    const width = 800;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    return window.open(
      pdfLink || "",
      "_blank",
      `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`
    );
  };

  const handleExportClick = (pdfLink) => {
    if (pdfLink) {
      openPdfWindow(pdfLink);
    } else {
      console.error("PDF link is not provided.");
    }
  };

  // Account History rows only carry `pdfLink` for tasks whose report has
  // already been generated; for the rest the report is generated on request.
  // That stays invisible to the user -- the button behaves exactly like the
  // plain download it always was: click -> the PDF opens. The popup is opened
  // synchronously (blank) before awaiting so browsers still treat it as part of
  // the click gesture instead of blocking it, then pointed at the S3 link the
  // generate call returns. Resolved links are cached per task id so a second
  // click on the same row doesn't hit the backend again.
  const handleDownloadPdf = async (history) => {
    const taskId = history?.taskId;
    const knownLink = history?.pdfLink || (taskId ? generatedPdfLinks[taskId] : "");

    if (knownLink) {
      handleExportClick(knownLink);
      return;
    }

    if (!taskId || pdfRequestsInFlightRef.current.has(taskId)) return;

    pdfRequestsInFlightRef.current.add(taskId);
    const pdfWindow = openPdfWindow();
    try {
      const { pdfLink, errorMessage } = await generateTaskPdfLink({
        taskId,
        accountNo: history?.accountNo ?? accountNumber,
      });
      if (!pdfLink) {
        pdfWindow?.close();
        toast.error(errorMessage || translations[langMode].pdfnotavailable);
        return;
      }
      setGeneratedPdfLinks((prev) => ({ ...prev, [taskId]: pdfLink }));
      if (pdfWindow) {
        pdfWindow.location.href = pdfLink;
      } else {
        // Popup blocked -- fall back to the current tab so the click still
        // lands on the PDF.
        window.open(pdfLink, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error generating task PDF:", error);
      pdfWindow?.close();
      toast.error(getBackendErrorMessage(error) || translations[langMode].failedtogeneratepdf);
    } finally {
      pdfRequestsInFlightRef.current.delete(taskId);
    }
  };
  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',

    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };

  const handleFileChange = async (event) => {
    const newFile = event.target.files[0];
    if (newFile) {
      setFile(newFile);
      setIsLoading(true);
      try {
        const responseData = await getFieldNames();
        if (responseData?.header?.code === 600) {
          setMandatoryCols(responseData.body.value.mandatoryColumns);
          setOtherCols(responseData.body.value.otherColumns);
          setMapOpen(true);

          const reader = new FileReader();
          reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const headers = [];
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            const firstRow = range.s.r;
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cell = worksheet[XLSX.utils.encode_cell({ r: firstRow, c: col })];
              const header = cell ? cell.v : `UNKNOWN ${col}`;
              headers.push(header);
            }

            setColHeaders(headers);
          };
          reader.readAsArrayBuffer(newFile);
        } else {
          toast.error(translations[langMode].failstofetchorg);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
        toast.error(translations[langMode].failstofetchorg);
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Card chrome shared by the department blocks, the project blocks and the workflow tables.
  const surfaceClass =
    theme === "dark"
      ? "bg-gray-800/60 border-gray-700"
      : theme === "high-contrast"
        ? "high-contrast border-2"
        : "bg-white border-gray-200 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]";
  const countPillClass = `shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
    theme === "dark"
      ? "bg-gray-700 text-gray-300"
      : theme === "high-contrast"
        ? "high-contrast"
        : "bg-gray-100 text-gray-600"
  }`;
  const sectionHeadingClass = `mb-3 flex items-center gap-2 text-sm font-semibold sm:text-base ${
    theme === "dark" ? "text-gray-100" : "text-gray-800"
  }`;

  const statusFilterOptions = useMemo(
    () => [
      { value: HISTORY_STATUS_FILTER.ALL, label: translations[langMode]?.all || "All" },
      { value: HISTORY_STATUS_FILTER.FINISHED, label: translations[langMode]?.finishedLabel || "Finished" },
      { value: HISTORY_STATUS_FILTER.NON_FINISHED, label: translations[langMode]?.uploadedLabel || "Uploaded" },
    ],
    [langMode]
  );

  return (
    <div className="flex min-h-full sm:h-full sm:min-h-0 w-full min-w-0 flex-col overflow-y-auto sm:overflow-hidden">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
      <div className="flex flex-col min-h-full sm:h-full sm:min-h-0 min-w-0 flex-1 gap-2 mx-2 mt-2 mb-2 sm:overflow-hidden sm:gap-3 sm:mx-4 sm:mt-3 sm:mb-3">
        {/* Card 1: Enter Account Number -- stays pinned on desktop, scrolls naturally on mobile */}
        <div
          className={`flex shrink-0 min-w-0 flex-col gap-3 rounded-[14px] border px-3 py-3 sm:gap-4 sm:px-5 sm:py-4 ${theme === "dark"
              ? "bg-gray-800/60 border-gray-700"
              : theme === "high-contrast"
                ? "high-contrast border-2"
                : "bg-white border-gray-200 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]"
            }`}
        >
          {headerSlot && React.cloneElement(headerSlot, {
            extraActions: (
              <CommonButton colorIntent="primary"
                onClick={handleImportClick}
                variant="success"
                tooltipText={translations[langMode].uploadtheaccountexcel}
                tooltipPosition="top"
                disabled={!customVarPerm?.canCreate}
                className={`w-full sm:w-auto justify-center !h-10 !min-h-[40px] !rounded-[10px] px-3 py-2 text-xs sm:px-4 sm:text-sm ${
                  theme === "dark"
                    ? "!border !bg-emerald-500/10 !text-emerald-400 !border-emerald-500/20 hover:!bg-emerald-500/20"
                    : theme === "high-contrast"
                      ? ""
                      : "!border !bg-emerald-50 !text-emerald-700 !border-emerald-100 hover:!bg-emerald-100"
                } ${!customVarPerm?.canCreate ? "cursor-not-allowed opacity-50" : ""}`}
              >
                <UploadCloud size={16} className="shrink-0" />
                {translations[langMode].importdb}
              </CommonButton>
            ),
          })}
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative w-full min-w-0 sm:flex-1 sm:min-w-[220px]">
                <Search
                  className={`absolute ${isRtl ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                  size={16}
                />
                <input
                  type="number"
                  className={`h-10 w-full box-border rounded-[10px] border text-sm leading-normal ${isRtl ? "pr-10 pl-3 text-right" : "pl-10 pr-3"} ${theme === "dark"
                      ? "dark-mode border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400"
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : "border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    }`}
                  onChange={accountNumberFetch}
                  value={accountNumber}
                  placeholder={translations[langMode].enteraccno}
                />
              </div>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <CommonButton colorIntent="primary"
                  onClick={fetchTaskHistory}
                  variant="primary"
                  tooltipText={translations[langMode].fetchtheaccounthistory}
                  tooltipPosition="top"
                  className="flex-1 justify-center !h-10 !min-h-[40px] !rounded-[10px] px-3 py-2 text-xs sm:flex-initial sm:w-auto sm:px-4 sm:text-sm"
                >
                  {isLoading && (
                    <svg
                      className="animate-spin h-5 w-5 text-white mr-2 shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {translations[langMode].submit}
                </CommonButton>
                <CommonButton colorIntent="warning"
                  onClick={clearData}
                  variant="danger"
                  tooltipText={translations[langMode].clearalldata}
                  tooltipPosition="top"
                  className={`flex-1 justify-center !h-10 !min-h-[40px] !rounded-[10px] px-3 py-2 text-xs sm:flex-initial sm:w-auto sm:px-4 sm:text-sm ${
                    theme === "dark"
                      ? "!border !bg-red-500/10 !text-red-400 !border-red-500/20 hover:!bg-red-500/20"
                      : theme === "high-contrast"
                        ? ""
                        : "!border !bg-red-50 !text-red-600 !border-red-100 hover:!bg-red-100"
                  }`}
                >
                  <RotateCcw size={15} className="shrink-0" />
                  {translations[langMode].reset}
                </CommonButton>
              </div>
            </div>

          </div>
          <div className={`w-full sm:w-auto inline-flex max-w-full self-start items-start gap-1.5 rounded-[8px] border px-2.5 py-1.5 text-[11px] leading-snug font-medium sm:text-xs lg:text-[12px] 2xl:text-[14px] ${
            theme === "dark"
              ? "bg-amber-950/15 border-amber-900/30 text-amber-300"
              : theme === "high-contrast"
                ? "bg-black border-white text-white"
                : "bg-amber-50 border-amber-200 text-amber-900"
          }`}>
            <Info
              size={13}
              strokeWidth={2.2}
              className={`mt-0.5 shrink-0 ${theme === "high-contrast" ? "text-white" : "text-amber-500 dark:text-amber-400"}`}
            />
            <span className="min-w-0">
              {isSuperAdmin()
                ? translations[langMode].accHistNoteAll
                : translations[langMode].accHistNoteScoped}
            </span>
          </div>
        </div>

        {/* Scrollable region: Department > Project > Account Details + per-workflow history -- internal scroll on desktop, natural page scroll on mobile */}
        <div className="flex flex-1 min-h-0 flex-col gap-2 sm:overflow-x-hidden sm:overflow-y-auto sm:gap-3">
        {isLoading ? (
          <Spinner />
        ) : accountDepartments.length === 0 ? (
          <div className={`min-w-0 rounded-[14px] border p-4 sm:p-5 ${surfaceClass}`}>
            <h3 className={`mb-4 text-base font-semibold break-words ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
              {translations[langMode].accDetail}
            </h3>
            <div className="flex min-h-[220px] w-full min-w-0 flex-col items-center justify-center gap-1.5 p-6 text-center sm:p-12">
              <Building2 size={30} strokeWidth={1.5} className={`mb-1 ${theme === "dark" ? "text-gray-600" : "text-gray-300"}`} />
              <p className={`max-w-[220px] text-sm font-semibold uppercase leading-snug sm:max-w-none ${theme === "dark" ? "text-gray-200" : "text-gray-800"}`}>
                {translations[langMode].noAccountDetialsAvailable.toUpperCase()}
              </p>
              <p className={`mt-1 max-w-[220px] text-sm leading-snug sm:max-w-xs ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                {translations[langMode].enterAccSubmitViewDetails}
              </p>
            </div>
          </div>
        ) : (
          accountDepartments.map((department, departmentIndex) => {
            const departmentKey = `department-${department.departmentId ?? departmentIndex}`;
            const isDepartmentCollapsed = collapsedSections.has(departmentKey);
            const departmentProjects = department.projects || [];
            const departmentTaskCount = departmentProjects.reduce(
              (total, project) => total + countWorkflowTasks(project.workflows),
              0
            );
            return (
            <div key={departmentKey} className={`min-w-0 rounded-[14px] border p-3 sm:p-5 ${surfaceClass}`}>
              {/* Level 1: Department */}
              <button
                type="button"
                onClick={() => toggleSection(departmentKey)}
                className="flex w-full min-w-0 items-center gap-3 text-left rtl:text-right"
              >
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${theme === "dark"
                      ? "bg-sky-500/15 text-sky-300"
                      : theme === "high-contrast"
                        ? "high-contrast"
                        : "bg-blue-50 text-[#236DB4]"
                    }`}
                >
                  <Building2 size={20} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block text-[11px] font-semibold uppercase tracking-wide ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                    {translations[langMode].department}
                  </span>
                  <span className={`block truncate text-base font-semibold sm:text-lg ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
                    {department.departmentName || "-"}
                  </span>
                </span>
                <span className={`hidden sm:inline-flex ${countPillClass}`}>
                  {translations[langMode].accHistProjects ?? "Projects"}: {departmentProjects.length}
                </span>
                <span className={`hidden sm:inline-flex ${countPillClass}`}>
                  {translations[langMode].accHistTasks ?? "Tasks"}: {departmentTaskCount}
                </span>
                <ChevronDown
                  size={18}
                  className={`shrink-0 opacity-60 transition-transform ${isDepartmentCollapsed ? "-rotate-90" : ""}`}
                />
              </button>

              {!isDepartmentCollapsed && (
              <div className="mt-4 flex flex-col gap-4">
                {departmentProjects.map((project, projectIndex) => {
                  const projectKey = `${departmentKey}-project-${project.projectId ?? projectIndex}`;
                  const isProjectCollapsed = collapsedSections.has(projectKey);
                  const accountDetails = project.details;
                  const projectWorkflows = project.workflows || [];
                  const projectAttachments = project.attachments || [];
                  const hasDynamicDetailColumns =
                    accountDetails?.accountDetailsJson &&
                    typeof accountDetails.accountDetailsJson === "object" &&
                    Object.keys(accountDetails.accountDetailsJson).length > 0;
                  return (
                  <div
                    key={projectKey}
                    className={`min-w-0 rounded-[12px] border border-l-4 p-3 sm:p-4 ${theme === "dark"
                        ? "border-gray-700 border-l-sky-400 bg-gray-900/40"
                        : theme === "high-contrast"
                          ? "high-contrast border-2"
                          : "border-gray-200 border-l-[#236DB4] bg-gray-50/70"
                      }`}
                  >
                    {/* Level 2: Project */}
                    <button
                      type="button"
                      onClick={() => toggleSection(projectKey)}
                      className="flex w-full min-w-0 items-center gap-3 text-left rtl:text-right"
                    >
                      <FolderOpen size={18} className={`shrink-0 ${theme === "dark" ? "text-sky-300" : "text-[#236DB4]"}`} />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-[11px] font-semibold uppercase tracking-wide ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                          {translations[langMode].project}
                        </span>
                        <span className={`block truncate text-sm font-semibold sm:text-base ${theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
                          {project.projectName || "-"}
                        </span>
                      </span>
                      <span className={`hidden sm:inline-flex ${countPillClass}`}>
                        {translations[langMode].workflows ?? "Workflows"}: {projectWorkflows.length}
                      </span>
                      <span className={`hidden sm:inline-flex ${countPillClass}`}>
                        {translations[langMode].accHistTasks ?? "Tasks"}: {countWorkflowTasks(projectWorkflows)}
                      </span>
                      <ChevronDown
                        size={16}
                        className={`shrink-0 opacity-60 transition-transform ${isProjectCollapsed ? "-rotate-90" : ""}`}
                      />
                    </button>

                    {!isProjectCollapsed && (
                    <div className="flex flex-col gap-5 pt-4">
                      {/* Account details of this project */}
                      <section>
                        <h3 className={sectionHeadingClass}>
                          {translations[langMode].accDetail}
                        </h3>
                        {accountDetails ? (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" dir={isRtl ? "rtl" : "ltr"}>
                  <>
                    {hasDynamicDetailColumns &&
                      Object.entries(accountDetails.accountDetailsJson).map(([key, value]) => {
                        const isAreaField = key.toLowerCase() === "area";
                        const isEmptyValue = value === undefined || value === null || value === "";
                        const FieldIcon = getDetailFieldIcon(key);
                        return (
                          <div key={key} className={detailCardClass(theme, isAreaField)}>
                            <div className={detailIconChipClass(theme)}>
                              <FieldIcon size={14} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className={detailLabelClass(theme)}>{key}</div>
                              {isEmptyValue ? (
                                <input
                                  type="text"
                                  name={key}
                                  value={value ?? ""}
                                  onChange={handleChange}
                                  className={`w-full rounded-lg border px-3 py-1.5 text-sm transition duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${theme === "dark"
                                      ? "border-gray-600 bg-gray-700 text-gray-200"
                                      : theme === "high-contrast"
                                        ? "high-contrast"
                                        : "border-gray-300 bg-white"
                                    }`}
                                  placeholder={`Enter ${key}`}
                                />
                              ) : isAreaField ? (
                                <div className={`${detailValueClass(theme)} line-clamp-2`} title={String(value)}>
                                  {value}
                                </div>
                              ) : (
                                <div className={detailValueClass(theme)}>{value}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                    <div className={detailCardClass(theme, false)}>
                      <div className={detailIconChipClass(theme)}>
                        <Clock3 size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={detailLabelClass(theme)}>
                          {translations[langMode].currenOs}
                          {translations[langMode].updatedat}
                        </div>
                        <div className={detailValueClass(theme)}>{formattedDate(accountDetails?.currentOsUpdatedAt)}</div>
                      </div>
                    </div>

                    <div className={detailCardClass(theme, false, true)}>
                      <div className={detailIconChipClass(theme, true)}>
                        <Gauge size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={detailLabelClass(theme)}>{translations[langMode].curros}</div>
                        <div className={detailValueClass(theme)}>{accountDetails?.currentOs ?? "-"}</div>
                      </div>
                    </div>

                    <div className={detailCardClass(theme, false)}>
                      <div className={detailIconChipClass(theme)}>
                        <Clock3 size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={detailLabelClass(theme)}>{translations[langMode].lastupdateddate}</div>
                        <div className={detailValueClass(theme)}>{formattedDate(accountDetails?.lastUpdatedDate)}</div>
                      </div>
                    </div>

                    <div className={detailCardClass(theme, false)}>
                      <div className={detailIconChipClass(theme)}>
                        <Clock3 size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={detailLabelClass(theme)}>{translations[langMode].lastupdatedtime}</div>
                        <div className={detailValueClass(theme)}>
                          {accountDetails?.lastUpdatedTime
                            ? moment(accountDetails.lastUpdatedTime, "HH:mm:ss").format("HH:mm")
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </>
                  </div>
                        ) : (
                          <div className={`rounded-[10px] border border-dashed px-4 py-6 text-center ${isRtl ? "text-base" : "text-sm"} ${theme === "dark" ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-500"}`}>
                            {translations[langMode].accHistNoProjectDetails ?? translations[langMode].noAccountDetialsAvailable}
                          </div>
                        )}
                      </section>

                      {/* Attachments uploaded for this project's account */}
                      {projectAttachments.length > 0 && (
                        <section>
                          <h3 className={sectionHeadingClass}>
                            <Paperclip size={15} className="shrink-0 opacity-70" />
                            {translations[langMode]?.attachments ?? "Attachments"}
                            <span className={countPillClass}>{projectAttachments.length}</span>
                          </h3>
                          <AttachmentList
                            attachments={projectAttachments}
                            resolveFresh={resolveFreshAttachment}
                            theme={theme}
                            langMode={langMode}
                          />
                        </section>
                      )}

                      {/* Level 3: workflow-wise task history of this project */}
                      <section>
                        <h3 className={sectionHeadingClass}>
                          {translations[langMode].accHistWorkflowHistory ?? "Workflow History"}
                          <span className={countPillClass}>{projectWorkflows.length}</span>
                        </h3>
                        {projectWorkflows.length === 0 ? (
                          <div className={`rounded-[10px] border border-dashed px-4 py-6 text-center text-sm ${theme === "dark" ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-500"}`}>
                            {translations[langMode]?.nohistory ?? "No task history available."}
                          </div>
                        ) : (
                <div className="flex flex-col gap-4">
                  {projectWorkflows.map((workflow, index) => {
                    const sectionKey = `${projectKey}-workflow-${index}`;
                    const isCollapsed = collapsedSections.has(sectionKey);
                    const taskCount = workflow.accountHistoryList?.length ?? 0;
                    const sectionQuery = (sectionSearch[sectionKey] || "").trim().toLowerCase();
                    const sectionFilter = sectionStatusFilter[sectionKey] || HISTORY_STATUS_FILTER.ALL;
                    const rows = workflow.accountHistoryList || [];
                    const rowsByStatus = rows.filter((h) => matchesHistoryStatusFilter(h?.taskStatus, sectionFilter));
                    const filteredRows = sectionQuery
                      ? rowsByStatus.filter((h) =>
                          [h.taskId, h.assigneeName, h.createdBy, h.remarks].some((v) =>
                            String(v ?? "").toLowerCase().includes(sectionQuery)
                          )
                        )
                      : rowsByStatus;
                    const uploadedDateHeaderText = translations[langMode].uploadeddate || "Uploaded Date";
                    const uploadedTimeHeaderText = translations[langMode].uploadedtime || "Uploaded Time";
                    const finishedDateHeaderText = translations[langMode].finisheddate;
                    const finishedTimeHeaderText = translations[langMode].fnishedtime;
                    const hasFinishedRows = rows.some((h) => isFinishedTaskStatus(h?.taskStatus));
                    const hasRowAttachments = rows.some((h) => h?.attachments?.length > 0);
                    const columnCount = (hasFinishedRows ? 10 : 8) + (hasRowAttachments ? 1 : 0);
                    return (
                    <div
                      key={index}
                      className={`min-w-0 rounded-[14px] border p-3 sm:p-5 ${theme === "dark"
                          ? "bg-gray-800/60 border-gray-700"
                          : theme === "high-contrast"
                            ? "high-contrast border-2"
                            : "bg-white border-gray-200 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]"
                        }`}
                    >
                      <div className={`flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3 ${isCollapsed ? "" : "mb-3 sm:mb-4"}`}>
                        <div className="flex min-w-0 items-center justify-between gap-2 sm:flex-1 sm:justify-start">
                          <button
                            type="button"
                            onClick={() => toggleSection(sectionKey)}
                            className="flex min-w-0 items-center gap-2 text-left rtl:text-right"
                          >
                            <ChevronDown
                              size={16}
                              className={`shrink-0 opacity-60 transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                            />
                            <h2
                              className={`min-w-0 truncate text-sm sm:text-base font-semibold ${theme === "dark" ? "text-gray-100" : theme === "high-contrast" ? "high-contrast" : "text-gray-800"
                                }`}
                            >
                              {workflow.workflowName} History
                            </h2>
                          </button>

                          <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold sm:hidden ${theme === "dark" ? "bg-gray-700 text-gray-300" : theme === "high-contrast" ? "high-contrast" : "bg-gray-100 text-gray-600"}`}>
                            {taskCount} {taskCount === 1 ? "task" : "tasks"}
                          </span>
                        </div>

                        {!isCollapsed && (
                          <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
                            <div className="relative min-w-0 flex-1 sm:w-44 sm:flex-initial">
                              <Search
                                size={14}
                                className={`pointer-events-none absolute ${isRtl ? "right-2.5" : "left-2.5"} top-1/2 -translate-y-1/2 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}
                              />
                              <input
                                type="text"
                                value={sectionSearch[sectionKey] || ""}
                                onChange={(e) =>
                                  setSectionSearch((prev) => ({ ...prev, [sectionKey]: e.target.value }))
                                }
                                placeholder={translations[langMode].search || "Search..."}
                                className={`h-[36px] w-full rounded-[8px] border text-xs ${isRtl ? "pr-8 pl-2 text-right" : "pl-8 pr-2"} ${theme === "dark"
                                    ? "dark-mode border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500"
                                    : theme === "high-contrast"
                                      ? "high-contrast"
                                      : "border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                  }`}
                              />
                            </div>
                            <div className="w-24 sm:w-[108px] shrink-0">
                              <ReactSelector
                                options={statusFilterOptions}
                                value={statusFilterOptions.find((opt) => opt.value === sectionFilter) || statusFilterOptions[0]}
                                onChange={(opt) =>
                                  setSectionStatusFilter((prev) => ({ ...prev, [sectionKey]: opt?.value || HISTORY_STATUS_FILTER.ALL }))
                                }
                                height="36px"
                                fontSize="12px"
                                borderRadius="8px"
                                menuPlacement="auto"
                                isSearchable={false}
                                isClearable={false}
                              />
                            </div>

                            <span className={`hidden sm:inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${theme === "dark" ? "bg-gray-700 text-gray-300" : theme === "high-contrast" ? "high-contrast" : "bg-gray-100 text-gray-600"}`}>
                              {taskCount} {taskCount === 1 ? "task" : "tasks"}
                            </span>
                          </div>
                        )}
                      </div>
                      {!isCollapsed && (
                      <TableScrollShell
                        theme={theme}
                        className="w-full min-w-0"
                        innerClassName="task-list-scrollbar max-h-none rounded-none border-0 shadow-none"
                        maxHeight="none"
                      >
                        <table className="w-full min-w-[760px] text-xs text-center border-collapse" dir={isRtl ? "rtl" : "ltr"}>
                          <thead
                            className={`border-b text-xs font-semibold uppercase tracking-[0.04em] ${theme === "dark"
                                ? "border-gray-700 bg-gray-800 text-gray-200"
                                : theme === "high-contrast"
                                  ? "high-contrast bg-gray-100"
                                  : "border-slate-200 bg-slate-50 text-slate-500"
                              }`}
                          >
                            <tr>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap first:pl-4 sm:first:pl-6">{translations[langMode].taskId}</th>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{translations[langMode].assingename}</th>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{uploadedDateHeaderText}</th>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{uploadedTimeHeaderText}</th>
                              {hasFinishedRows && (
                                <>
                                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{finishedDateHeaderText}</th>
                                  <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{finishedTimeHeaderText}</th>
                                </>
                              )}
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{translations[langMode].createBy}</th>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap min-w-[120px]">{translations[langMode].remark}</th>
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{translations[langMode].Status}</th>
                              {hasRowAttachments && (
                                <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap">{translations[langMode]?.attachments ?? "Attachments"}</th>
                              )}
                              <th className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-nowrap whitespace-nowrap last:pr-4 sm:last:pr-6">{translations[langMode].downloadPdf}</th>
                            </tr>
                          </thead>
                          <tbody className={theme === "dark" ? "bg-gray-900/50" : theme === "high-contrast" ? "" : "bg-white"}>
                            {filteredRows.length === 0 && (sectionQuery || sectionFilter !== HISTORY_STATUS_FILTER.ALL) ? (
                              <tr>
                                <td colSpan={columnCount} className={`px-4 py-6 text-center text-xs ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                                  No matching tasks found
                                </td>
                              </tr>
                            ) : (
                            filteredRows.map((history, idx) => (
                              <tr
                                key={idx}
                                className={`text-xs transition-colors duration-150 ${theme === "dark"
                                    ? "border-b border-gray-700 hover:bg-gray-700/50 bg-gray-900/50"
                                    : theme === "high-contrast"
                                      ? "high-contrast hover:opacity-90"
                                      : "border-b border-slate-100 bg-white hover:bg-slate-50/70"
                                  }`}
                              >
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 font-medium whitespace-nowrap first:pl-4 sm:first:pl-6 text-slate-700 dark:text-gray-200">
                                  {history.taskId ? (
                                    <HoverTooltip text={translations[langMode].taskRoadmapTooltip}>
                                      <button
                                        type="button"
                                        onClick={() => openRoadmap(history.taskId)}
                                        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 -mx-1.5 text-xs font-semibold underline decoration-dotted underline-offset-[3px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#236DB4]/40 ${
                                          theme === "dark"
                                            ? "text-sky-400 hover:bg-sky-500/10 hover:text-sky-300"
                                            : theme === "high-contrast"
                                              ? "high-contrast"
                                              : "text-[#236DB4] hover:bg-blue-50 hover:text-[#1b5691]"
                                        }`}
                                      >
                                        {history.taskId}
                                        <Route size={12} className="shrink-0 opacity-70" />
                                      </button>
                                    </HoverTooltip>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 text-slate-500 dark:text-gray-400">
                                  <AssigneeAvatarStack
                                    names={(history.assigneeName || "").split(",").map((n) => n.trim()).filter(Boolean)}
                                  />
                                </td>
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                  {formattedDate(history.uploadedDate)}
                                </td>
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                  {formatTimeOnly(history.uploadedTime)}
                                </td>
                                {hasFinishedRows && (
                                  <>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                      {isFinishedTaskStatus(history.taskStatus) ? formattedDate(history.finishedDate) : "-"}
                                    </td>
                                    <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                      {isFinishedTaskStatus(history.taskStatus) ? formatTimeOnly(history.finishedTime) : "-"}
                                    </td>
                                  </>
                                )}
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                  {history?.createdBy ?? "N/A"}
                                </td>
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 max-w-[200px] text-slate-500 dark:text-gray-400">{history?.remarks ?? '-'}</td>
                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap">
                                  <StatusPill success={history?.success} taskStatus={history?.taskStatus} />
                                </td>
                                {hasRowAttachments && (
                                  <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap text-slate-500 dark:text-gray-400">
                                    {history?.attachments?.length > 0 ? (
                                      <AttachmentList
                                        attachments={history.attachments}
                                        resolveFresh={resolveFreshAttachment}
                                        theme={theme}
                                        langMode={langMode}
                                        compact
                                      />
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                )}

                                <td className="px-3 sm:px-5 py-2.5 sm:py-3.5 whitespace-nowrap last:pr-4 sm:last:pr-6">
                                  {history.pdfLink ||
                                  (history.taskId && isFinishedTaskStatus(history.taskStatus)) ? (
                                    <HoverTooltip text={translations[langMode].donwlaodthetaskreportinpdfformat}>
                                      <CommonButton colorIntent="primary"
                                        onClick={() => handleDownloadPdf(history)}
                                        variant="primary"
                                        className="!h-9 !min-h-[36px] !rounded-[8px] gap-1.5 px-2.5 py-1.5 text-xs sm:px-4 sm:text-sm whitespace-nowrap"
                                      >
                                        <Download size={13} className="shrink-0" />
                                        {translations[langMode].download}
                                      </CommonButton>
                                    </HoverTooltip>
                                  ) : (
                                    <span className="text-gray-400 whitespace-nowrap text-xs">
                                      {translations[langMode]?.pdfNotAvailable ?? "PDF is not available"}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                            )}
                          </tbody>
                        </table>
                      </TableScrollShell>
                      )}
                    </div>
                    );
                  })}
                </div>
                        )}
                      </section>
                    </div>
                    )}
                  </div>
                  );
                })}
              </div>
              )}
            </div>
            );
          })
        )}
        </div>
      </div>
      {mapOpen && (
        <ColumnMap
          open={mapOpen}
          setOpen={setMapOpen}
          mandatoryCols={mandatoryCols}
          otherCols={otherCols}
          file={file} // Pass the file to ColumnMap
          colHeaders={colHeaders} // Pass headers to ColumnMap
        />
      )}
      <TaskRoadmapDrawer
        open={roadmapOpen}
        taskId={roadmapTaskId}
        onClose={() => setRoadmapOpen(false)}
      />
      {loading && <Spinner />}
    </div>
  );
};

export default TaskHistory;
